// Plate patterns with per-position class templates ('D' digit, 'A' letter, 'X' either).
// Templates drive both matching and confusable correction (spec §6.2–6.3).
// Estonian is first-class; then a configurable EU set; then a permissive fallback
// that locks only with a stricter vote share.

export type CharClass = 'D' | 'A' | 'X';

export interface PlatePattern {
  id: string;
  /** All accepted shape templates, keyed by length at match time. */
  templates: readonly string[];
  /** Permissive fallback patterns need a stricter lock share. */
  fallback?: boolean;
}

function lettersThenDigits(letters: [number, number], digits: [number, number]): string[] {
  const out: string[] = [];
  for (let a = letters[0]; a <= letters[1]; a++)
    for (let d = digits[0]; d <= digits[1]; d++) out.push('A'.repeat(a) + 'D'.repeat(d));
  return out;
}

export const PATTERNS: readonly PlatePattern[] = [
  // Estonia: 123ABC
  { id: 'EE', templates: ['DDDAAA'] },
  // Finland: AB(C)-123
  { id: 'FI', templates: lettersThenDigits([2, 3], [2, 3]) },
  // Latvia: XX-1234 (1–4 digits)
  { id: 'LV', templates: lettersThenDigits([2, 2], [1, 4]).filter((t) => t !== 'AADD' && t !== 'AADDD') },
  // Lithuania: ABC-123
  { id: 'LT', templates: ['AAADDD'] },
  // Germany: 2–5 letters then 1–4 digits (city + series), total 4–8
  {
    id: 'DE',
    templates: lettersThenDigits([2, 5], [1, 4]).filter((t) => t.length >= 4 && t.length <= 8),
  },
  // Sweden: ABC12D (new) or ABC123 (old)
  { id: 'SE', templates: ['AAADDA', 'AAADDD'] },
  // Poland: 2–3 letters then 4–5 alphanumerics
  {
    id: 'PL',
    templates: ['AAXXXX', 'AAXXXXX', 'AAAXXXX', 'AAAXXXXX'],
  },
  // Permissive fallback (spec: ^[A-Z0-9]{4,9}$), stricter vote threshold
  {
    id: 'ANY',
    templates: Array.from({ length: 6 }, (_, i) => 'X'.repeat(i + 4)),
    fallback: true,
  },
] as const;

// Note on ordering: FI/LV/LT/DE share letters-then-digits shapes, so an ambiguous
// read (e.g. AB123) matches the first pattern whose template fits. The pattern id
// is a shape label used for vote grouping and thresholds — not a country claim;
// region attribution comes from the OCR region head.

export function classOf(ch: string): CharClass {
  return ch >= '0' && ch <= '9' ? 'D' : 'X';
}

export function charMatches(ch: string, cls: CharClass): boolean {
  if (cls === 'X') return /^[A-Z0-9]$/.test(ch);
  if (cls === 'D') return ch >= '0' && ch <= '9';
  return ch >= 'A' && ch <= 'Z';
}
